import { EntityLink } from './entity-link';
import { Tooltip } from './tooltip';

/**
 * O que uma linha de `entity_event` precisa expor para dizer quem agiu. Tipo
 * estrutural de propósito: as três telas que o usam têm entidades próprias
 * (`UserTimelineEvent`, `TransactionEvent`, …) que já casam com esta forma.
 */
export interface EventActorSource {
  actorPlatformProfileId: string | null;
  actorName?: string | null;
  actorUserId?: string | null;
  realActorName?: string | null;
  realActorUserId?: string | null;
}

/**
 * Quem realizou a ação, numa linha de histórico de auditoria. Substitui o
 * rótulo genérico `• Admin`, que jogava fora o ator que o backend já mandava —
 * e que, na linha de `ACCOUNT_TEMPORARY_PASSWORD_SET`, era a única informação
 * que importava.
 *
 * Quatro estados, nesta ordem de precedência:
 *
 * 1. **sistema** (`actorPlatformProfileId === null`) → `• Sistema`, como antes;
 * 2. **sob act-as** → o humano real **e** a máscara, os dois linkados: o audit
 *    log grava os dois e esconder qualquer um dos lados mente sobre a ação
 *    (ver `docs/reference/impersonation.md` no monolith);
 * 3. **humano** → o nome, linkado para o painel dele;
 * 4. **ator não resolvido** (perfil apagado depois do evento) → o uuid em mono,
 *    como antes — a informação não some porque o nome sumiu.
 *
 * O link abre em nova aba: estas linhas vivem dentro de modais, e navegar na
 * mesma aba fecharia o modal.
 */
export function EventActor({ event }: { event: EventActorSource }) {
  if (event.actorPlatformProfileId === null) return <span>• Sistema</span>;

  if (event.realActorName) {
    return (
      <span>
        •{' '}
        <ActorName name={event.realActorName} userId={event.realActorUserId} />{' '}
        {/*
          O tooltip envolve só o termo que ele explica, não a frase inteira: o
          trigger dele é `inline-flex`, e com dois nomes completos dentro o
          bloco deixaria de quebrar linha e estouraria a largura do modal.
          O `tabIndex` é o que leva o termo à ordem de tab: o modo `hover` só
          envolve `children` num `<span>`, e texto puro não recebe foco.
        */}
        <Tooltip message="Ação registrada sob act-as" trigger="hover">
          <span tabIndex={0}>agindo como</span>
        </Tooltip>{' '}
        <ActorName
          name={event.actorName}
          userId={event.actorUserId}
          fallbackId={event.actorPlatformProfileId}
        />
      </span>
    );
  }

  return (
    <span>
      •{' '}
      <ActorName
        name={event.actorName}
        userId={event.actorUserId}
        fallbackId={event.actorPlatformProfileId}
      />
    </span>
  );
}

/**
 * O nome, linkado quando há pessoa para apontar. Sem `userId` não há link
 * possível; sem `name` mostra-se o id que veio, em mono — é o que sobra quando
 * o perfil foi apagado depois do evento.
 */
function ActorName({
  name,
  userId,
  fallbackId,
}: {
  name?: string | null;
  userId?: string | null;
  fallbackId?: string;
}) {
  if (!name) return <span className="font-mono">{fallbackId}</span>;

  return (
    <EntityLink kind="user" id={userId} newTab fallback={name}>
      {name}
    </EntityLink>
  );
}
